// Guest passes' server state (walk-ins without an account), as TanStack Query hooks over the generated client —
// `packages/contracts/src/routes/guest-passes.ts`:
//
//   GET  …/venues/{v}/guest-passes                        the venue's live guest passes, or one sale's   passes:read
//   POST …/passes/holdings/{h}/refund                     Void: the existing refund of the guest's pass   passes:refund
//
// The list also says whether the venue serves guests at all (`guestsEnabled`), which the counter reads to offer guest
// passes: a cashier reads no settings.

import type { GuestPassList, RefundPassRequest } from '@clavian/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';

export const guestKeys = {
  all: (organizationId: string, venueId: string) => ['guests', organizationId, venueId] as const,
  list: (organizationId: string, venueId: string, saleId: string | undefined) => ['guests', organizationId, venueId, saleId ?? 'live'] as const,
};

/** How often a sale's codes are read again while its payment is settling (the pass is issued just after it). */
const SETTLING_POLL_MS = 1000;

/**
 * The venue's live guest passes, or — with `saleId` — one sale's, in any state. While a sale's guest pass still waits for
 * its payment to settle, it is read again every second, so the code shows as working as soon as it does.
 */
export function useGuestPasses(organizationId: string, venueId: string, options: { readonly saleId?: string; readonly enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery<GuestPassList>({
    queryKey: guestKeys.list(organizationId, venueId, options.saleId),
    queryFn: () => api.listGuestPasses({ path: { organizationId, venueId }, ...(options.saleId === undefined ? {} : { query: { saleId: options.saleId } }) }),
    enabled: options.enabled ?? true,
    staleTime: 15_000,
    refetchInterval: (query) =>
      options.saleId !== undefined && (query.state.data?.guestPasses ?? []).some((pass) => pass.status === 'awaiting_payment') ? SETTLING_POLL_MS : false,
  });
}

/** Void a guest pass: refund its holding through the existing refund, then read the venue's guests again. */
export function useVoidGuestPass(organizationId: string, venueId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ holdingId, body }: { readonly holdingId: string; readonly body: RefundPassRequest }) =>
      api.refundPassHolding({ path: { organizationId, holdingId }, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: guestKeys.all(organizationId, venueId) }),
  });
}
